import { useEffect } from "react";
import { AIMessage, AssistantMessage } from "@interfaces/AIAssistTypes";

const MESSAGE_SCROLL_OFFSET = 4;

const findScrollContainer = (element: Element | null): Element | null => {
  let scrollContainer: Element | null = element;
  let foundContainer: Element | null = null;

  while (scrollContainer) {
    const style = window.getComputedStyle(scrollContainer);
    const hasScroll = scrollContainer.scrollHeight > scrollContainer.clientHeight;

    if (
      hasScroll &&
      (style.overflowY === "auto" ||
        style.overflowY === "scroll" ||
        scrollContainer.classList.contains("ion-content") ||
        scrollContainer.tagName === "ION-CONTENT")
    ) {
      foundContainer = scrollContainer;
      break;
    }

    if (scrollContainer.shadowRoot) {
      const shadowScroll = scrollContainer.shadowRoot.querySelector(".inner-scroll, [part='scroll']");
      if (shadowScroll) {
        foundContainer = shadowScroll as Element;
        break;
      }
    }

    scrollContainer = scrollContainer.parentElement;
  }

  return foundContainer;
};

const scrollToElement = (scrollAnchor: HTMLElement, offset: number = 0) => {
  const foundContainer = findScrollContainer(scrollAnchor.parentElement);

  if (foundContainer && foundContainer !== document.body && foundContainer !== document.documentElement) {
    const rect = scrollAnchor.getBoundingClientRect();
    const containerRect = foundContainer.getBoundingClientRect();
    const anchorTop = rect.top - containerRect.top + (foundContainer as HTMLElement).scrollTop;
    (foundContainer as HTMLElement).scrollTo({
      top: Math.max(0, anchorTop - offset),
      behavior: "smooth",
    });
  } else {
    const rect = scrollAnchor.getBoundingClientRect();
    const anchorTop = rect.top + window.scrollY;
    window.scrollTo({
      top: Math.max(0, anchorTop - offset),
      behavior: "smooth",
    });
  }
};

export const useAIMessageScroll = (message: AIMessage, hasAnimated: boolean) => {
  // Special scroll behavior for evidence-preview-anchor: scroll to top of page
  useEffect(() => {
    const isAssistantMsg = "type" in message;
    const isEvidencePreview = isAssistantMsg && (message as AssistantMessage).type === "evidence-preview";

    if (!isEvidencePreview || hasAnimated) return;

    const scrollAnchorId = `${message.id}-evidence-preview-anchor`;

    const timeoutId = setTimeout(() => {
      const scrollAnchor = document.getElementById(scrollAnchorId);
      if (!scrollAnchor) return;

      scrollToElement(scrollAnchor as HTMLElement, 0);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [hasAnimated, message.id, message]);

  // Scroll message into view if it's off-screen when it first appears
  useEffect(() => {
    if (hasAnimated) return;

    const isAssistantMsg = "type" in message;
    const assistantMessage = isAssistantMsg ? (message as AssistantMessage) : null;

    if (assistantMessage?.type === "evidence-preview-save") return;

    // Handled by separate effect above
    if (assistantMessage?.type === "evidence-preview") return;

    const scrollAnchorId = `${message.id}-scroll-anchor`;

    const timeoutId = setTimeout(() => {
      const scrollAnchor = document.getElementById(scrollAnchorId);
      if (!scrollAnchor) return;

      const rect = scrollAnchor.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const isOffScreen = rect.bottom < 0 || rect.top > viewportHeight;

      if (isOffScreen) {
        scrollToElement(scrollAnchor as HTMLElement, MESSAGE_SCROLL_OFFSET);
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [hasAnimated, message.id, message]);
};
